import {
  type FacetSelection,
  NIL_UUID,
  type SoupSearchRequest,
} from '@app/features/soup';
import type { SearchSoupQueryArgs } from '@queries/soup/search';
import type {
  EntityFilters,
  NotificationFilters,
} from '@service-search/generated/models';
import type { HomeTab, HomeTypeFilter } from '../types';
import type { HomeQueryCapabilities, HomeViewContext } from './home-query';

const tabTypes: Record<HomeTab, ReadonlySet<HomeTypeFilter>> = {
  signal: new Set([
    'documents',
    'tasks',
    'email',
    'channels',
    'chats',
    'agents',
    'projects',
    'github',
    'calendar',
  ]),
  noise: new Set(['email']),
};

function readFilter(selection: FacetSelection): boolean | undefined {
  const active = selection.read ?? [];
  if (active.length !== 1) return undefined;
  if (active[0] === 'read') return true;
  if (active[0] === 'unread') return false;

  return undefined;
}

function resolveTypes(
  tab: HomeTab,
  selection: FacetSelection,
  capabilities: HomeQueryCapabilities
): ReadonlySet<HomeTypeFilter> {
  const selected = new Set(selection.type ?? []);
  const requested =
    selected.size === 0
      ? tabTypes[tab]
      : new Set([...tabTypes[tab]].filter((type) => selected.has(type)));

  return new Set(
    [...requested].filter((type) => {
      if (type === 'calendar') return capabilities.calendar;
      if (type === 'github') return capabilities.foreignEntities;
      return true;
    })
  );
}

/** Mirrors Home tab/type/read semantics for service-backed search. */
export function buildHomeSearchRequest(
  context: HomeViewContext,
  search: SoupSearchRequest
): SearchSoupQueryArgs {
  const types = resolveTypes(context.tab, context.facets, context.capabilities);
  const notification = readFilter(context.facets);

  const filtersIncompleteEntities =
    context.tab === 'signal' || context.tab === 'noise';

  const notificationFilters: NotificationFilters = {
    states:
      notification === false
        ? ['unseen']
        : notification === true
          ? filtersIncompleteEntities
            ? ['seen']
            : ['seen', 'done']
          : filtersIncompleteEntities
            ? ['unseen', 'seen']
            : [],
  };

  const documentTypes = [...types].filter(
    (type) => type === 'documents' || type === 'tasks'
  );

  const hasNotificationFilter =
    filtersIncompleteEntities || notification !== undefined;

  const filters: EntityFilters = {
    agent_session_filters: { ids: [NIL_UUID] },
    calendar_event_filters: { calendar_event_ids: [NIL_UUID] },
    call_filters: { call_ids: [NIL_UUID] },
    channel_filters: { channel_ids: [NIL_UUID] },
    channel_thread_filters: { thread_ids: [NIL_UUID] },
    chat_filters: { chat_ids: [NIL_UUID] },
    crm_company_filters: { company_ids: [NIL_UUID] },
    document_filters: { document_ids: [NIL_UUID] },
    email_filters: { email_thread_ids: [NIL_UUID] },
    foreign_entity_filters: { ids: [NIL_UUID] },
    project_filters: { project_ids: [NIL_UUID] },
  };

  if (types.has('calendar')) {
    filters.calendar_event_filters = {};
  }

  if (types.has('agents')) {
    filters.agent_session_filters = { include: true };
  }

  if (types.has('channels')) {
    const channelFilters: NonNullable<EntityFilters['channel_filters']> = {};

    if (context.tab === 'signal') {
      channelFilters.is_participant = true;
    }

    if (hasNotificationFilter) {
      channelFilters.notification_filters = notificationFilters;
    }

    filters.channel_filters = channelFilters;
  }

  if (types.has('channels')) {
    filters.channel_thread_filters = {
      participant_ids: [context.userId ?? NIL_UUID],
    };
  }

  if (types.has('chats')) {
    const chatFilters: NonNullable<EntityFilters['chat_filters']> = {};

    if (hasNotificationFilter) {
      chatFilters.notification_filters = notificationFilters;
    }

    filters.chat_filters = chatFilters;
  }

  if (documentTypes.length > 0) {
    const documentFilters: NonNullable<EntityFilters['document_filters']> = {};

    if (documentTypes.length === 1 && documentTypes[0] === 'tasks') {
      documentFilters.sub_types = ['task'];
    }

    if (hasNotificationFilter) {
      documentFilters.notification_filters = notificationFilters;
    }

    filters.document_filters = documentFilters;
  }

  if (types.has('email')) {
    const emailFilters: NonNullable<EntityFilters['email_filters']> = {
      shared: 'exclude',
    };

    if (context.tab === 'signal') {
      emailFilters.importance = true;
    }

    if (context.tab === 'noise') {
      emailFilters.importance = false;
    }

    // Email inbox/read state is independent of user notifications. Applying
    // a notification predicate here would drop email that has no notification.
    if (notification !== undefined) emailFilters.is_read = notification;

    filters.email_filters = emailFilters;
  }

  if (types.has('github')) {
    const foreignEntityFilters: NonNullable<
      EntityFilters['foreign_entity_filters']
    > = {
      foreign_entity_sources: ['github_pull_request'],
      includes_me: true,
    };

    if (hasNotificationFilter) {
      foreignEntityFilters.notification_filters = notificationFilters;
    }

    filters.foreign_entity_filters = foreignEntityFilters;
  }

  if (types.has('projects')) {
    const projectFilters: NonNullable<EntityFilters['project_filters']> = {};

    if (hasNotificationFilter) {
      projectFilters.notification_filters = notificationFilters;
    }

    filters.project_filters = projectFilters;
  }

  return {
    params: { cursor: null, page_size: 100 },
    body: {
      query: search.query,
      match_type: search.matchType,
      search_on: 'name_content',
      filters,
    },
  };
}
